import type {
  GroundMode,
  GroundTicketParseResult,
  GroundTrip,
  GroundTripLookupResult,
  TrainStopsResult,
} from '../../types/ground';
import { fetchAsyncJob } from './asyncJob';
import { API_URL, fetchApi } from './client';

export type {
  GroundMode,
  GroundTicketParseResult,
  GroundTrip,
  GroundTripLookupResult,
  TrainStopsResult,
};

export const lookupGroundTrip = async (
  routeNumber: string,
  date: string,
  lang?: string,
  opts?: { originCity?: string; destinationCity?: string },
): Promise<GroundTripLookupResult> => {
  const qs = new URLSearchParams({ routeNumber, date });
  if (lang) qs.set('lang', lang);
  if (opts?.originCity?.trim()) qs.set('originCity', opts.originCity.trim());
  if (opts?.destinationCity?.trim()) qs.set('destinationCity', opts.destinationCity.trim());
  return fetchApi<GroundTripLookupResult>(`${API_URL}/ground-trips/lookup?${qs}`);
};

export const getGroundTrips = async (mode?: GroundMode): Promise<GroundTrip[]> => {
  const qs = mode ? `?mode=${encodeURIComponent(mode)}` : '';
  return fetchApi<GroundTrip[]>(`${API_URL}/ground-trips${qs}`);
};

export const getGroundTripTags = async (mode: GroundMode): Promise<{ tags: string[] }> => {
  return fetchApi<{ tags: string[] }>(`${API_URL}/ground-trips/tags?mode=${encodeURIComponent(mode)}`);
};

export const createGroundTrip = async (
  trip: Omit<GroundTrip, 'id'>
): Promise<GroundTrip> => {
  return fetchApi<GroundTrip>(`${API_URL}/ground-trips`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(trip),
  });
};

export const updateGroundTrip = async (
  id: number,
  trip: Partial<Omit<GroundTrip, 'id' | 'mode'>> & { mode?: GroundMode }
): Promise<GroundTrip> => {
  return fetchApi<GroundTrip>(`${API_URL}/ground-trips/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(trip),
  });
};

export const deleteGroundTrip = async (id: number): Promise<void> => {
  await fetchApi<void>(`${API_URL}/ground-trips/${id}`, {
    method: 'DELETE',
  });
};

export const parseGroundTicket = async (
  file: File,
  mode: GroundMode,
  lang?: string,
): Promise<GroundTicketParseResult> => {
  const form = new FormData();
  form.append('file', file);
  const params = new URLSearchParams({ mode });
  if (lang) params.set('lang', lang);
  return fetchAsyncJob<GroundTicketParseResult>(
    `${API_URL}/ground-trips/parse-ticket?${params}`,
    { method: 'POST', body: form },
    { timeoutMs: 180_000 },
  );
};

/** Resolve exact train route via RZD and cache on the trip (once). */
export const resolveGroundTripRoute = async (
  tripId: number,
  opts?: { force?: boolean; lang?: string },
): Promise<TrainStopsResult> => {
  const qs = new URLSearchParams();
  if (opts?.force) qs.set('force', 'true');
  if (opts?.lang) qs.set('lang', opts.lang);
  const q = qs.toString();
  return fetchAsyncJob<TrainStopsResult>(
    `${API_URL}/ground-trips/${tripId}/resolve-route${q ? `?${q}` : ''}`,
    { method: 'POST' },
    { timeoutMs: 120_000 },
  );
};

/** @deprecated use resolveGroundTripRoute — kept for compatibility */
export const lookupTrainStops = async (params: {
  routeNumber: string;
  originCity?: string | null;
  destinationCity?: string | null;
  date?: string | null;
  operator?: string | null;
  lang?: string;
}): Promise<TrainStopsResult> => {
  // Legacy body endpoint removed; callers should use resolveGroundTripRoute(tripId).
  void params;
  return { stops: [], stations: [], trainName: null, source: 'none', cached: false };
};
